import {
  useGetNotificationsBadge,
  useGetNotificationsInfinite,
} from "api/notifications/service";
import classNames from "classnames";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT_ACTIVE,
  THEME2_TOOLBAR_TRIGGER_HOVER_BG_DARK_SURFACE,
  THEME2_TOOLBAR_TRIGGER_HOVER_BG_LIGHT_SURFACE,
  THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE,
  THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE,
} from "./adminNavbarTheme2Appearance";

const mapNotification = (notification) => ({
  id: notification.id,
  title: notification.title,
  message: notification.content,
  time: new Date(notification.createdAt).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }),
  type: "info",
  read: notification.is_read,
  link: notification.link || null,
});

const Notification = ({
  lightNavbarSurface = false,
  theme2Shell = false,
  toolbarIconFontSize,
  toolbarIconColor,
  toolbarIconColorActive,
  ...props
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [classes, setClasses] = React.useState("dropdown");
  const isDropdownOpen = classes === "dropdown show";
  const { unreadCount: badgeUnreadCount } = useGetNotificationsBadge(true);
  const {
    results,
    unreadCount: listUnreadCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isSuccess: isListSuccess,
  } = useGetNotificationsInfinite(isDropdownOpen);

  const [darkMode, setDarkMode] = React.useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : true;
  });
  const [triggerHovered, setTriggerHovered] = React.useState(false);
  const menuRef = React.useRef(null);
  const listRef = React.useRef(null);

  const notifications = React.useMemo(
    () => (results || []).map(mapNotification),
    [results]
  );

  const badgeCount = isDropdownOpen ? listUnreadCount : badgeUnreadCount;

  React.useEffect(() => {
    if (isDropdownOpen && isListSuccess) {
      queryClient.invalidateQueries({ queryKey: ["notifications", "badge"] });
    }
  }, [isDropdownOpen, isListSuccess, listUnreadCount, queryClient]);

  const handleClick = () => {
    if (classes === "dropdown") {
      setClasses("dropdown show");
    } else {
      setClasses("dropdown");
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.link) {
      if (
        notification.link.startsWith("http://") ||
        notification.link.startsWith("https://")
      ) {
        window.open(notification.link, "_blank");
      } else {
        navigate(notification.link);
      }
    }
    setClasses("dropdown");
  };

  const handleListScroll = React.useCallback(
    (event) => {
      const el = event.currentTarget;
      if (!el || !hasNextPage || isFetchingNextPage) return;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
      if (nearBottom) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const getNotificationIcon = (read) => {
    return read ? "tim-icons icon-check-2" : "tim-icons icon-bell-55";
  };

  const getNotificationColor = (read) => {
    if (read) {
      return "text-success";
    }
    switch (props.activeColor) {
      case "blue":
        return "text-primary";
      case "green":
        return "text-success";
      case "orange":
        return "text-warning";
      case "red":
        return "text-danger";
      case "purple":
        return "text-info";
      default:
        return "text-primary";
    }
  };

  React.useEffect(() => {
    if (classes === "dropdown show") {
      const handleOutsideClick = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setClasses("dropdown");
        }
      };
      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
  }, [classes]);

  React.useEffect(() => {
    if (darkMode) {
      document.body.classList.remove("white-content");
    } else {
      document.body.classList.add("white-content");
    }
  }, [darkMode]);

  const hoverBg = lightNavbarSurface
    ? THEME2_TOOLBAR_TRIGGER_HOVER_BG_LIGHT_SURFACE
    : THEME2_TOOLBAR_TRIGGER_HOVER_BG_DARK_SURFACE;
  const triggerBg = triggerHovered
    ? hoverBg
    : lightNavbarSurface
      ? classes.includes("show")
        ? "rgba(0, 0, 0, 0.08)"
        : "transparent"
      : classes.includes("show")
        ? "rgba(255, 255, 255, 0.15)"
        : "transparent";
  const triggerIconColor = (() => {
    if (toolbarIconColor != null) {
      if (classes.includes("show")) return toolbarIconColorActive ?? "#1d8cf8";
      if (triggerHovered && lightNavbarSurface)
        return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE;
      if (triggerHovered) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE;
      return toolbarIconColor;
    }
    if (classes.includes("show")) return "#1d8cf8";
    if (triggerHovered && lightNavbarSurface)
      return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE;
    if (triggerHovered) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE;
    return lightNavbarSurface ? "rgba(0, 0, 0, 0.55)" : "white";
  })();

  const t2 = theme2Shell;
  const panelScrollbarColor = t2
    ? lightNavbarSurface
      ? "rgba(0, 0, 0, 0.35) rgba(0, 0, 0, 0.06)"
      : "rgba(255, 255, 255, 0.45) rgba(0, 0, 0, 0.2)"
    : "#1d8cf8 #172b4d";
  const badgeRingBorder = t2
    ? lightNavbarSurface
      ? "2px solid #fff"
      : "2px solid #313a46"
    : lightNavbarSurface
      ? "2px solid #fff"
      : "2px solid #172b4d";

  return (
    <div className="settings" style={{}} ref={menuRef}>
      <div className={classes}>
        <a
          href="#pablo"
          onClick={(e) => {
            e.preventDefault();
            handleClick();
          }}
          onMouseEnter={() => setTriggerHovered(true)}
          onMouseLeave={() => setTriggerHovered(false)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            backgroundColor: triggerBg,
            transition: "all 0.2s ease",
            position: "relative",
          }}
        >
          <i
            className="tim-icons icon-bell-55"
            style={{
              fontSize: toolbarIconFontSize ?? "1.5rem",
              color: triggerIconColor,
              ...(t2 && !lightNavbarSurface && classes.includes("show")
                ? { fontWeight: THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT_ACTIVE }
                : {}),
            }}
          />

          {badgeCount > 0 && (
            <span
              className="notification"
              style={{
                position: "absolute",
                top: "0px",
                right: "0px",
                background:
                  props.activeColor === "blue"
                    ? "#1d8cf8"
                    : props.activeColor === "green"
                      ? "#00f2c3"
                      : props.activeColor === "orange"
                        ? "#ff8d72"
                        : props.activeColor === "red"
                          ? "#f5365c"
                          : props.activeColor === "purple"
                            ? "#e14eca"
                            : "#1d8cf8",
                color: "white",
                height: "18px",
                width: "18px",
                fontSize: "10px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: badgeRingBorder,
              }}
            >
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </a>
        <ul
          ref={listRef}
          className={classNames(
            "dropdown-menu show",
            t2 && "dtiq-theme2-notifications-menu"
          )}
          onScroll={handleListScroll}
          style={{
            position: "absolute",
            right: "0",
            paddingBottom: "16px",
            zIndex: 9999,
            marginTop: "100px",
            minWidth: "350px",
            maxWidth: "calc(100vw - 40px)",
            maxHeight: "400px",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            scrollbarWidth: "thin",
            scrollbarColor: panelScrollbarColor,
          }}
        >
          {isLoading && notifications.length === 0 ? (
            <li
              className={classNames(
                "text-center w-100",
                t2 ? "dtiq-theme2-notifications-empty py-3" : "py-3"
              )}
            >
              <div
                className="spinner-border spinner-border-sm text-muted"
                role="status"
              >
                <span className="sr-only">Loading...</span>
              </div>
            </li>
          ) : notifications.length === 0 ? (
            <li
              className={classNames(
                "text-center w-100",
                t2 ? "dtiq-theme2-notifications-empty py-3" : "py-3"
              )}
            >
              <i
                className="tim-icons icon-bell-55 text-muted"
                style={{ fontSize: "2rem" }}
              />
              <p className="text-muted mt-2 mb-0">No notifications</p>
            </li>
          ) : (
            <>
              {notifications.map((notification) => (
                <li
                  key={notification.id}
                  className={
                    t2
                      ? classNames(
                          "notification-item dtiq-theme2-notifications-item",
                          !notification.read &&
                            "dtiq-theme2-notifications-item--unread"
                        )
                      : `notification-item pb-3 pt-3 pl-2 pr-2 ${
                          !notification.read ? "unread" : ""
                        }`
                  }
                  style={
                    t2
                      ? { width: "100%", cursor: "pointer" }
                      : {
                          borderBottom: "1px solid rgba(255,255,255,0.1)",
                          cursor: "pointer",
                          backgroundColor: !notification.read
                            ? "rgba(33, 76, 118, 0.1)"
                            : "transparent",
                          width: "100%",
                        }
                  }
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="d-flex align-items-start">
                    <div
                      className={`notification-icon ${getNotificationColor(
                        notification.read
                      )}`}
                    >
                      <i className={getNotificationIcon(notification.read)} />
                    </div>
                    <div className="notification-content flex-grow-1 ml-0">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <p
                          className={classNames(
                            "mb-0",
                            t2 && "dtiq-theme2-notifications-title"
                          )}
                          style={
                            t2
                              ? { marginBottom: "0px" }
                              : {
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "white",
                                  marginBottom: "0px",
                                }
                          }
                        >
                          {notification.title}
                        </p>
                        <div className="d-flex align-items-center">
                          <small
                            className={classNames(
                              "mr-0",
                              t2
                                ? "dtiq-theme2-notifications-time"
                                : "text-muted"
                            )}
                            style={t2 ? undefined : { fontSize: "11px" }}
                          >
                            {notification.time}
                          </small>
                        </div>
                      </div>
                      <p
                        className={classNames(
                          "mb-0",
                          t2
                            ? "dtiq-theme2-notifications-body"
                            : "text-muted"
                        )}
                        style={
                          t2
                            ? undefined
                            : {
                                fontSize: "13px",
                                lineHeight: "1.4",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                wordBreak: "break-word",
                              }
                        }
                      >
                        {notification.message}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
              {(hasNextPage || isFetchingNextPage) && (
                <li className="text-center w-100 py-2">
                  {isFetchingNextPage ? (
                    <div
                      className="spinner-border spinner-border-sm text-muted"
                      role="status"
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    <small className="text-muted">Scroll for more</small>
                  )}
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Notification;
